import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { VerifyEmailNotice } from '@metabus/community-admin'
import { useAuth } from '@/lib/auth/AuthProvider'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { BRAND_NAME, BRAND_LOGO, PRODUCT_TITLE } from '@/lib/brand'

export const LoginPage = () => {
  const {
    signIn,
    signInWithEmail,
    signOut,
    isAuthenticated,
    isLoading,
    error,
    needsEmailVerification,
    unverifiedEmail,
  } = useAuth()
  const navigate = useNavigate()
  const [showEmailLogin, setShowEmailLogin] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard')
    }
  }, [isAuthenticated, navigate])

  // An unconfirmed address is not a permissions problem, so it does not get the
  // permissions message. This user can finish signing in on their own, and the
  // session is still live, so the resend call still has a token to send.
  if (needsEmailVerification) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <VerifyEmailNotice
              email={unverifiedEmail}
              brandName={PRODUCT_TITLE}
              onSignOut={() => void signOut()}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  const handleGoogleSignIn = async () => {
    try {
      await signIn()
    } catch (error) {
      console.error('Sign in error:', error)
    }
  }

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await signInWithEmail(email, password)
    } catch (error) {
      console.error('Sign in error:', error)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {/* RULE 0: BRAND_LOGO defaults to "" so the buildgravity build renders
              text-only exactly as before; the metabus build supplies a path and
              shows the mark above the title. */}
          {BRAND_LOGO && (
            <img
              src={BRAND_LOGO}
              alt={BRAND_NAME}
              className="mx-auto mb-3 h-14 w-auto"
            />
          )}
          <CardTitle className="text-3xl">{PRODUCT_TITLE}</CardTitle>
          <CardDescription>
            Sign in to access the Sales Engine
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {!showEmailLogin ? (
            <>
              <Button
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                className="w-full"
                size="lg"
              >
                {isLoading ? 'Signing in...' : 'Sign in with Google'}
              </Button>
              <Button
                onClick={() => setShowEmailLogin(true)}
                variant="outline"
                className="w-full"
                size="lg"
              >
                Email
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Use your {BRAND_NAME} Google account to sign in
              </p>
            </>
          ) : (
            <form onSubmit={handleEmailSignIn} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="email"
                  type="email"
                  placeholder="test@buildgravity.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  data-testid="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button
                type="submit"
                data-testid="sign-in"
                disabled={isLoading}
                className="w-full"
                size="lg"
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </Button>
              <Button
                type="button"
                onClick={() => setShowEmailLogin(false)}
                variant="ghost"
                className="w-full"
              >
                Back to Google
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
